import React from "react";
import { useMemo } from "react";
import { Spin, Empty } from "antd";
import styles from "../imagePicker.module.css";
import { RowsPhotoAlbum, ColumnsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import "react-photo-album/columns.css";
import { useMedia } from "react-use";

const ASSET_BASE_URL = "https://api.qureal.com";

const resolveImageSrc = (img) => {
    return (
        img?.url ||
        img?.src?.medium || // pexels
        img?.previewURL || // pixabay
        img?.urls?.small || // unsplash
        img?.media?.[0]?.tinygif?.url || // tenor
        (img?.id ? `${ASSET_BASE_URL}/assets/${img.id}` : null) // directus
    );
};

export default function ImageGrid({
    images = [],
    loading = false,
    selectedImages = [],
    onSelect,
}) {
    //columns view for mobile screens
    const isMobile = useMedia("(max-width: 640px)");

    //  data → react-photo-album format
    const albumPhotos = useMemo(() => {
        return images
            .map((img) => {
                const src = resolveImageSrc(img);
                if (!src) return null;

                return {
                    src,
                    width: img.width || img.imageWidth || 400,
                    height: img.height || img.imageHeight || 300,
                    raw: img, // keep original image
                    id: img.id || img.urls?.raw || src,
                };
            })
            .filter(Boolean);
    }, [images]);

    if (loading) {
        return (
            <div className={styles.center}>
                <Spin size="large" />
            </div>
        );
    }

    if (!albumPhotos.length) {
        return (
            <div className={styles.center}>
                <Empty description="No images found" />
            </div>
        );
    }

    const selectedIds = new Set(
        selectedImages.map(
            (img) => img?.id || img?.urls?.raw || resolveImageSrc(img)
        )
    );

    const renderImage = {
        image: ({ style, ...props }, { photo }) => {
            const isSelected = selectedIds.has(photo.id);

            return (
                <div
                    style={{
                        position: "relative",
                        borderRadius: 8,
                        overflow: "visible",
                    }}
                >
                    <img
                        {...props}
                        style={{
                            ...style,
                            width: "100%",
                            height: "auto",
                            position: "relative",
                            zIndex: 1,
                            borderRadius: 6,
                            cursor: "pointer",
                            background: "#f3f3f3",
                            border: isSelected
                                ? "3px solid #00998e"
                                : "3px solid transparent",
                            boxSizing: "border-box",
                            transition: "border 0.2s ease",
                        }}
                        alt="image"
                    />

                    {isSelected && (
                        <div
                            style={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                background: "#00998e",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 16 16">
                                <path
                                    d="M13.5 4L6 11.5L2.5 8"
                                    stroke="white"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}
                </div>
            );
        },
    };

    return isMobile ? (
        <ColumnsPhotoAlbum
            photos={albumPhotos}
            // columns={(w) => (w < 420 ? 2 : 3)}
            columns={3}
            spacing={isMobile ? 4 : 8}
            padding={0}
            render={renderImage}
            onClick={({ photo }) => {
                onSelect?.({
                    ...photo.raw,
                    id: photo.id,
                });
            }}
        />
    ) : (
        <RowsPhotoAlbum
            photos={albumPhotos}
            spacing={12}
            padding={0}
            targetRowHeight={140}
            render={renderImage}
            onClick={({ photo }) => {
                onSelect?.({
                    ...photo.raw,
                    id: photo.id,
                });
            }}
        />
    );
}
//made render as variable to avoid re-creation on each render
